import { FC, useCallback, useRef, useState } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  ScrollViewProps,
} from 'react-native';

import { StaticImage, StaticImages } from 'components/picture/static-image';
import RefreshControl from 'components/pull-to-reload/refresh-control';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { isNative } from 'utils/environment';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

/** can be passed into a ListView to enable more data loading at end of scrolling  */
export const useLoadMoreAtBottom = (dataLoader: () => void) => {
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
      const bottomOffset = Platform.select({ android: 100, ios: 0, web: 0 });

      if (
        layoutMeasurement.height + contentOffset.y >=
        contentSize.height - (bottomOffset as number)
      ) {
        dataLoader();
      }
    },
    [dataLoader]
  );
  return { onScroll };
};

export interface IPullToReload {
  onRefresh(): Promise<any>;
  isDisabled?: boolean;
  /** increase this if you can see the refresh control on the page */
  progressViewOffset?: number;
}

export const PullToRefreshContainer = Box.withConfig<{ isRefreshing: boolean }>(p => ({
  position: 'absolute',
  minHeight: p.isRefreshing ? '50px' : '0px',
  left: 0,
  right: 0,
}));

export const usePullToReload = ({
  onRefresh,
  isDisabled = false,
  progressViewOffset,
}: IPullToReload) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPullingDown, setIsPullingDown] = useState(false);
  const [hidePullMessage, setHidePullMessage] = useState(false);
  const { formatMessage } = useIntl();

  // calls original refresh function but makes sure it sticks around for loading for 1s to give user feedback
  const wrappedOnRefresh = useCallback(() => {
    setIsRefreshing(true);
    hapticNotification({ type: HapticsNotificationType.SUCCESS });

    // for good loading indicator UX and prove to user something happened
    const minDuration = new Promise<void>(resolve => setTimeout(resolve, 750));

    // returning promise for react-native-web-refresh-control, react native api doesn't use.
    return Promise.all([onRefresh(), minDuration]).then(() => {
      setIsRefreshing(false);
      setHidePullMessage(true);
      setIsPullingDown(false);
      setTimeout(() => setHidePullMessage(false), 1000); // the built in animation time for ios to contract refresh loader
    });
  }, [onRefresh]);

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;

    if (offsetY < 0) {
      setIsPullingDown(true);
    }
  }, []);

  const isMobile = useIsMobileBreakpoint();

  if (!isMobile || isDisabled) {
    return {};
  }

  const message = formatMessage({ id: 'pullToRefresh' });

  const children = !hidePullMessage && isNative && (
    <PullToRefreshContainer isRefreshing={isRefreshing}>
      {!isRefreshing && isPullingDown && (
        <>
          <Text fontWeight="bold" textAlign="center">
            {message}
          </Text>
          <StaticImage alt={message} source={StaticImages.PullDownToReload} />
        </>
      )}
      {isRefreshing && <StaticImage alt={message} source={StaticImages.PullDownReloading} />}
    </PullToRefreshContainer>
  );

  const refreshControl = (
    <RefreshControl
      refreshing={isRefreshing}
      onRefresh={wrappedOnRefresh}
      // react-native-web-refresh-control doesn't support the below
      {...(isNative ? { progressViewOffset, tintColor: 'transparent', children } : {})}
    />
  );

  return {
    handleScroll,
    refreshControl,
    isRefreshing,
  };
};

/**
 * - ✅: if you don't already have a ScrollView / FlatList and you need "Pull to reload"
 * - ❌: If you just need pull to reload, use the usePullToReload hook instead
 */
export const PullToReloadScrollView: FC<React.PropsWithChildren<
  ScrollViewProps & IPullToReload
>> = ({ onRefresh, onScroll, isDisabled, progressViewOffset, ...props }) => {
  const scrollViewRef = useRef<ScrollView>(null);

  const { refreshControl, handleScroll } = usePullToReload({
    isDisabled,
    onRefresh,
    progressViewOffset,
  });

  return (
    <ScrollView
      ref={scrollViewRef}
      nestedScrollEnabled
      showsVerticalScrollIndicator={false}
      refreshControl={refreshControl}
      scrollEventThrottle={17}
      onScroll={event => {
        if (onScroll) {
          onScroll(event);
        }
        if (handleScroll) {
          handleScroll(event);
        }
      }}
      {...props}
    />
  );
};
